import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Grid, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AppsIcon from "@mui/icons-material/Apps";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SecurityIcon from "@mui/icons-material/Security";
import SearchIcon from "@mui/icons-material/Search";
import TimerIcon from "@mui/icons-material/Timer";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { useAppSelector } from "../store";

const FEATURES = [
  {
    icon: <AccessTimeIcon sx={{ fontSize: 40 }} />,
    titleKey: "home.featureTimeBased",
    descKey: "home.featureTimeBasedDesc",
  },
  {
    icon: <AppsIcon sx={{ fontSize: 40 }} />,
    titleKey: "home.featureServices",
    descKey: "home.featureServicesDesc",
  },
  {
    icon: <RocketLaunchIcon sx={{ fontSize: 40 }} />,
    titleKey: "home.featureOneClick",
    descKey: "home.featureOneClickDesc",
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    titleKey: "home.featureSecure",
    descKey: "home.featureSecureDesc",
  },
];

const STEPS = [
  {
    icon: <SearchIcon sx={{ fontSize: 36 }} />,
    titleKey: "home.step1Title",
    descKey: "home.step1Desc",
  },
  {
    icon: <TimerIcon sx={{ fontSize: 36 }} />,
    titleKey: "home.step2Title",
    descKey: "home.step2Desc",
  },
  {
    icon: <PlayCircleIcon sx={{ fontSize: 36 }} />,
    titleKey: "home.step3Title",
    descKey: "home.step3Desc",
  },
];

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 3,
          py: 8,
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            mb: 2,
            fontSize: { xs: "2rem", md: "3.5rem" },
          }}
        >
          {t("home.heroTitle")}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            maxWidth: 600,
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {t("home.heroDescription")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate(isAuthenticated ? "/services" : "/login")}
            sx={{ px: 4, py: 1.5, fontWeight: 600 }}
          >
            {t("home.getStarted")}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/services")}
            sx={{ px: 4, py: 1.5, fontWeight: 600 }}
          >
            {t("home.browseServices")}
          </Button>
        </Box>
      </Box>
      {/* Features */}
      <Box sx={{ px: 3, py: 8, bgcolor: "action.hover" }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            mb: 6,
          }}
        >
          {t("home.featuresTitle")}
        </Typography>
        <Grid
          container
          spacing={3}
          sx={{
            justifyContent: "center",
          }}
        >
          {FEATURES.map((f) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.titleKey}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  height: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                }}
              >
                <Box sx={{ color: "primary.main", mb: 2 }}>{f.icon}</Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {t(f.titleKey)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {t(f.descKey)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
      {/* How It Works */}
      <Box sx={{ px: 3, py: 8 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            mb: 6,
          }}
        >
          {t("home.howItWorksTitle")}
        </Typography>
        <Grid
          container
          spacing={4}
          sx={{
            justifyContent: "center",
          }}
        >
          {STEPS.map((s, i) => (
            <Grid size={{ xs: 12, md: 4 }} key={s.titleKey}>
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  {s.icon}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                >
                  Step {i + 1}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mt: 0.5,
                    mb: 1,
                  }}
                >
                  {t(s.titleKey)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    maxWidth: 300,
                    mx: "auto",
                  }}
                >
                  {t(s.descKey)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      {/* CTA */}
      <Box
        sx={{
          px: 3,
          py: 8,
          textAlign: "center",
          bgcolor: "action.hover",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 2,
          }}
        >
          {t("home.ctaTitle")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 4,
            maxWidth: 500,
            mx: "auto",
          }}
        >
          {t("home.ctaDescription")}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate(isAuthenticated ? "/dashboard" : "/login")}
          sx={{ px: 5, py: 1.5, fontWeight: 600 }}
        >
          {t("home.getStarted")}
        </Button>
      </Box>
    </Box>
  );
};

export default HomePage;
